import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../controls/Button';
import InputBox from '../../controls/InputBox';
import GuideSerialNumberModal from '../../modals/GuideSerialNumber';

/**
 * Header component.
 * @returns - React element that represents the Header component.
 */
function Header(): ReactElement {
  return (
    <div className="flex flex-col items-center">
      <div className="text-white text-2xl font-bold">
        <span className="text-primary-500">Step 1:</span> Add devices & configure
      </div>
      <div className="text-gray-400 mt-1">
        Set up your TelosAir device to start collecting real-time air quality data.
      </div>
    </div>
  );
}

/**
 * AddDevice component.
 * @returns - React element that represents the AddDevice component.
 */
export default function OnboardingAddDevice(): ReactElement {
  const navigate = useNavigate();
  const [started, setStarted] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [deviceAdded, setDeviceAdded] = useState(false);
  const [isGuideSerialNumberModalOpen, setIsGuideSerialNumberModalOpen] = useState(false);
  if (deviceAdded) {
    return (
      <div className="flex flex-col flex-1  items-center">
        <Header />
        <div className="mt-5 flex flex-1 max-h-[40.813rem] flex-col rounded bg-container-500 w-[66.66vw]">
          <div className="flex items-center justify-between h-fit p-4 w-full bg-container-700 border-b border-gray-900">
            <div className="text-sm text-gray-600">
              Device List: <span className="text-gray-300">04 Devices</span> | Connected:{' '}
              <span className="text-gray-300">0</span>
            </div>
            <div className="flex gap-2">
              <div className="w-[18.5rem]">
                <InputBox
                  noMarginTop
                  idname="serial-number"
                  value={serialNumber}
                  onChnage={(value) => setSerialNumber(value)}
                  placeholder="Serial Number"
                />
              </div>

              <Button
                name="Add Device"
                theme="secondary-gray"
                size="sm"
                onClick={() => {
                  setDeviceAdded(true);
                  setSerialNumber('');
                }}
              />
            </div>
          </div>
          <div className="max-h-full overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="bg-container-700 font-medium text-gray-400 text-xs sticky top-0">
                <tr>
                  <th className="h-11 text-center border-t border-b border-gray-900 w-1/3">
                    Serial Number
                  </th>
                  <th className="h-11 text-center border-t border-b border-gray-900 w-1/3">
                    Device No.
                  </th>
                  <th className="h-11 text-center border-t border-b border-gray-900 w-1/3">
                    Conn. Status
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-300 font-medium text-sm">
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>

                <tr>
                  <td className="h-20 text-center border-b border-gray-900">1234-1234-1234-1234</td>
                  <td className="h-20 text-center border-b border-gray-900">Gateway - #1234</td>
                  <td className="h-20 text-center border-b border-gray-900 text-error-500">
                    Inactive
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="my-6">
          <Button
            onClick={() => {
              navigate('/onboarding/connect-internet');
            }}
            name="Connect devices to internet "
            theme="primary"
            size="sm"
          />
        </div>
      </div>
    );
  }
  if (started) {
    return (
      <div className="flex flex-col flex-1 items-center">
        <Header />
        <div className="mt-12 flex flex-col justify-center items-center">
          <div className="text-white text-xl font-semibold">Enter your device’s serial number</div>
          <div className="mt-3 w-[18.5rem]">
            <InputBox
              noMarginTop
              idname="serial-number"
              value={serialNumber}
              onChnage={(value) => setSerialNumber(value)}
              placeholder="Serial Number"
            />
          </div>
          <div className="mt-3">
            <Button
              onClick={() => {
                setDeviceAdded(true);
                setSerialNumber('');
              }}
              name="Add Device"
              theme="primary"
              size="sm"
            />
          </div>
          <div className="mt-5 text-gray-400">
            Can’t find the serial number?{' '}
            <button
              className="text-primary-500"
              type="button"
              onClick={() => setIsGuideSerialNumberModalOpen(true)}
            >
              Click here
            </button>
          </div>
        </div>
        {isGuideSerialNumberModalOpen && (
          <GuideSerialNumberModal handleClose={() => setIsGuideSerialNumberModalOpen(false)} />
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col flex-1 justify-center items-center gap-9">
      <div className="h-[35.188rem] w-[35.188rem] bg-background-700 rounded-xl" />
      <div>
        <h1 className="text-2xl font-bold text-center text-white">Welcome to TelosAir!</h1>
        <p className="text-gray-400 text-center">
          Let’s get your device online and start monitoring air quality
        </p>
      </div>
      <div className="mt-3">
        <Button
          onClick={() => setStarted(true)}
          name="Get started"
          theme="primary"
          size="md"
          isBlockButton
        />
      </div>
    </div>
  );
}
